/* ============================================================================
 * PROJECT DATA FILTERING HELPERS
 * Filtering, search, and render-only visibility projection utilities for nodes
 * ============================================================================ */

import {
  NodeDisplayVisibility,
  NodeDisplayWorkstream,
  NodeFilterMatchKind,
  ProjectNode,
  ProjectNodeWithComputed,
  ViewFilter,
  VisibilityTier,
  Workstream,
} from '@/shared/types/projectData';
import {
  getNodeAssigneeDisplayNames,
  getNodeAssignees,
  AGENT_DELEGATE_ASSIGNEE_ID,
  isAgentAssigneeId,
  isNodeUnassigned,
  UNASSIGNED_ASSIGNEE_FILTER_VALUE,
} from '@/shared/utils/projectDataUtils';
import { hasActiveFilters, normalizeViewFilter } from '@/shared/utils/filterState';

interface NodeVisibilityProjection {
  displayVisibility: NodeDisplayVisibility;
  subtreeVisibilityState: ProjectNodeWithComputed['subtreeVisibilityState'];
  hasAccessibleDescendants: boolean;
}

interface NodeWorkstreamProjection {
  displayWorkstream: NodeDisplayWorkstream;
  subtreeWorkstreamState: ProjectNodeWithComputed['subtreeWorkstreamState'];
}

const normalizeVisibilityTier = (value?: string | null): VisibilityTier =>
  value === 'partner' || value === 'client' ? value : 'internal';

const getNodeVisibilityTier = (node: ProjectNode): VisibilityTier => normalizeVisibilityTier(node.visibilityTier);
const normalizeWorkstream = (value?: string | null): Workstream =>
  value === 'development' || value === 'meeting' || value === 'sales' || value === 'admin' || value === 'planning' || value === 'research'
    ? value
    : 'general';
const getNodeWorkstream = (node: ProjectNode): Workstream => normalizeWorkstream(node.workstream);

const attachProjection = (
  node: ProjectNode,
  projection: NodeVisibilityProjection | undefined,
  workstreamProjection: NodeWorkstreamProjection | undefined,
  filterMatchKind: NodeFilterMatchKind,
): ProjectNodeWithComputed => ({
  ...node,
  filterMatchKind,
  subtreeVisibilityState: projection?.subtreeVisibilityState || 'uniform',
  displayVisibility: projection?.displayVisibility || getNodeVisibilityTier(node),
  subtreeWorkstreamState: workstreamProjection?.subtreeWorkstreamState || 'uniform',
  displayWorkstream: workstreamProjection?.displayWorkstream || getNodeWorkstream(node),
  hasAccessibleDescendants: projection?.hasAccessibleDescendants || false,
});

export const buildNodeVisibilityProjectionMap = (
  nodes: ProjectNode[],
): Map<string, NodeVisibilityProjection> => {
  const nodeMap = new Map<string, ProjectNode>();
  const childIdsByParent = new Map<string | null, string[]>();

  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
    const parentId = node.parentId || null;
    const existingChildIds = childIdsByParent.get(parentId) || [];
    existingChildIds.push(node.id);
    childIdsByParent.set(parentId, existingChildIds);
  });

  const projectionMap = new Map<string, NodeVisibilityProjection>();

  const visit = (nodeId: string): Set<VisibilityTier> => {
    const node = nodeMap.get(nodeId);
    if (!node) {
      return new Set<VisibilityTier>();
    }

    const subtreeTiers = new Set<VisibilityTier>([getNodeVisibilityTier(node)]);
    const childIds = childIdsByParent.get(nodeId) || [];

    childIds.forEach((childId) => {
      visit(childId).forEach((tier) => subtreeTiers.add(tier));
    });

    const uniformTier = subtreeTiers.size === 1 ? Array.from(subtreeTiers)[0] : null;
    projectionMap.set(nodeId, {
      subtreeVisibilityState: uniformTier ? 'uniform' : 'mixed',
      displayVisibility: uniformTier || 'mixed',
      hasAccessibleDescendants: childIds.length > 0,
    });

    return subtreeTiers;
  };

  (childIdsByParent.get(null) || []).forEach((rootId) => visit(rootId));

  nodes.forEach((node) => {
    if (!projectionMap.has(node.id)) {
      projectionMap.set(node.id, {
        subtreeVisibilityState: 'uniform',
        displayVisibility: getNodeVisibilityTier(node),
        hasAccessibleDescendants: false,
      });
    }
  });

  return projectionMap;
};

export const buildNodeWorkstreamProjectionMap = (
  nodes: ProjectNode[],
): Map<string, NodeWorkstreamProjection> => {
  const nodeMap = new Map<string, ProjectNode>();
  const childIdsByParent = new Map<string | null, string[]>();

  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
    const parentId = node.parentId || null;
    const existingChildIds = childIdsByParent.get(parentId) || [];
    existingChildIds.push(node.id);
    childIdsByParent.set(parentId, existingChildIds);
  });

  const projectionMap = new Map<string, NodeWorkstreamProjection>();

  const visit = (nodeId: string): Set<Workstream> => {
    const node = nodeMap.get(nodeId);
    if (!node) {
      return new Set<Workstream>();
    }

    const subtreeWorkstreams = new Set<Workstream>([getNodeWorkstream(node)]);
    const childIds = childIdsByParent.get(nodeId) || [];

    childIds.forEach((childId) => {
      visit(childId).forEach((workstream) => subtreeWorkstreams.add(workstream));
    });

    const uniformWorkstream = subtreeWorkstreams.size === 1 ? Array.from(subtreeWorkstreams)[0] : null;
    projectionMap.set(nodeId, {
      subtreeWorkstreamState: uniformWorkstream ? 'uniform' : 'mixed',
      displayWorkstream: uniformWorkstream || 'mixed',
    });

    return subtreeWorkstreams;
  };

  (childIdsByParent.get(null) || []).forEach((rootId) => visit(rootId));

  nodes.forEach((node) => {
    if (!projectionMap.has(node.id)) {
      projectionMap.set(node.id, {
        subtreeWorkstreamState: 'uniform',
        displayWorkstream: getNodeWorkstream(node),
      });
    }
  });

  return projectionMap;
};

/**
 * Filter nodes based on user permissions
 */
export const filterNodesByPermissions = (
  nodes: ProjectNode[],
  hasPermission: (resource: string, action: string) => boolean
): ProjectNode[] => {
  return nodes.filter(node => {
    switch (node.type) {
      case 'task':
        return hasPermission('tasks', 'read');
      case 'document':
        return hasPermission('documents', 'read');
      case 'milestone':
        return hasPermission('calendar', 'read');
      case 'group':
        return true;
      default:
        return hasPermission('tasks', 'read');
    }
  });
};

/**
 * Check if a node matches the given filters and search query
 */
export const nodeMatchesFilters = (
  node: ProjectNode,
  filters: ViewFilter,
  searchQuery: string
): boolean => {
  // Company filter - check projectClient first (company-first multi-project label), then fallback to client (legacy)
  if (filters.client?.length) {
    const nodeClient = node.projectClient || (node as ProjectNode & { client?: string }).client;
    if (!nodeClient || !filters.client.includes(nodeClient)) {
      return false;
    }
  }
  
  // Status filter
  if (filters.statuses?.length) {
    const nodeStatus = node.completionState || node.status;
    const hasStatusMatch = filters.statuses.includes(nodeStatus as ProjectNode['status']) ||
      (filters.statuses.includes('todo') && nodeStatus === 'not-started') ||
      (filters.statuses.includes('done') && nodeStatus === 'completed');
    if (!hasStatusMatch) return false;
  }
  
  // Priority filter
  if (filters.priorities?.length && (!node.priority || !filters.priorities.includes(node.priority))) {
    return false;
  }
  
  // Assignee filter
  if (filters.assignees?.length) {
    const nodeAssignees = getNodeAssignees(node);
    const wantsUnassigned = filters.assignees.includes(UNASSIGNED_ASSIGNEE_FILTER_VALUE);
    const selectedAssignees = filters.assignees.filter((assignee) => assignee !== UNASSIGNED_ASSIGNEE_FILTER_VALUE);
    const wantsAgent = selectedAssignees.includes(AGENT_DELEGATE_ASSIGNEE_ID);
    const hasAssigneeMatch = nodeAssignees.some(assignee => (
      selectedAssignees.includes(assignee) || (wantsAgent && isAgentAssigneeId(assignee))
    ));
    if (!(hasAssigneeMatch || (wantsUnassigned && isNodeUnassigned(node)))) {
      return false;
    }
  }
  
  // Type filter
  if (filters.types?.length && !filters.types.includes(node.type)) {
    return false;
  }
  
  // Tag filter
  if (filters.tags?.length && (!node.tags || !filters.tags.some(tag => node.tags?.includes(tag)))) {
    return false;
  }

  if (filters.workstreams?.length) {
    const nodeWorkstream = node.workstream || 'general';
    if (!filters.workstreams.includes(nodeWorkstream)) {
      return false;
    }
  }

  if (filters.developmentRoles?.length) {
    const nodeDevelopmentRoles = node.developmentRoles || [];
    if (!nodeDevelopmentRoles.some((role) => filters.developmentRoles!.includes(role))) {
      return false;
    }
  }

  if (filters.visibilityTiers?.length) {
    const nodeVisibility = node.visibilityTier || 'internal';
    if (!filters.visibilityTiers.includes(nodeVisibility)) {
      return false;
    }
  }
  
  // Search query
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    return Boolean(
      node.title?.toLowerCase().includes(query) ||
      node.description?.toLowerCase().includes(query) ||
      getNodeAssigneeDisplayNames(node).some(assignee => assignee.toLowerCase().includes(query)) ||
      node.assignee?.toLowerCase().includes(query) ||
      node.tags?.some(tag => tag.toLowerCase().includes(query)) ||
      node.workstream?.toLowerCase().includes(query) ||
      node.developmentRoles?.some((role) => role.toLowerCase().includes(query)) ||
      node.visibilityTier?.toLowerCase().includes(query)
    );
  }
  
  return true;
};

/**
 * Include parent nodes to maintain hierarchy
 */
export const includeParentNodes = (
  nodeId: string,
  nodeMap: Map<string, ProjectNode>,
  matchingNodeIds: Set<string>
): void => {
  const node = nodeMap.get(nodeId);
  if (node && node.parentId && !matchingNodeIds.has(node.parentId)) {
    matchingNodeIds.add(node.parentId);
    includeParentNodes(node.parentId, nodeMap, matchingNodeIds);
  }
};

/**
 * Apply filters and search to nodes
 */
export const applyFiltersAndSearch = (
  nodes: ProjectNode[],
  filters: ViewFilter,
  searchQuery: string
): ProjectNodeWithComputed[] => {
  const normalizedFilters = normalizeViewFilter(filters);
  const hasFilters = hasActiveFilters(normalizedFilters, searchQuery);
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  const visibilityProjectionMap = buildNodeVisibilityProjectionMap(nodes);
  const workstreamProjectionMap = buildNodeWorkstreamProjectionMap(nodes);

  if (!hasFilters) {
    return nodes.map((node) =>
      attachProjection(
        node,
        visibilityProjectionMap.get(node.id),
        workstreamProjectionMap.get(node.id),
        'direct'
      )
    );
  }

  const directMatchIds = new Set<string>();
  const includedNodeIds = new Set<string>();

  nodes.forEach((node) => {
    if (nodeMatchesFilters(node, normalizedFilters, searchQuery)) {
      directMatchIds.add(node.id);
      includedNodeIds.add(node.id);
    }
  });

  Array.from(directMatchIds).forEach((nodeId) => {
    includeParentNodes(nodeId, nodeMap, includedNodeIds);
  });

  return nodes
    .filter((node) => includedNodeIds.has(node.id))
    .map((node) =>
      attachProjection(
        node,
        visibilityProjectionMap.get(node.id),
        workstreamProjectionMap.get(node.id),
        directMatchIds.has(node.id) ? 'direct' : 'ancestor-context',
      )
    );
};
